import { useEffect, useMemo, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Users } from 'lucide-react';

const categorias = [
  { key: 'sub14', label: 'Sub 14', limite: 13 },
  { key: 'sub16', label: 'Sub 16', limite: 15 },
  { key: 'sub18', label: 'Sub 18', limite: 17 },
  { key: 'sub21', label: 'Sub 21', limite: 20 },
  { key: 'adulto', label: 'Adulto', limite: 21 },
];

function idadeNoAno(dataNascimento) {
  if (!dataNascimento) return null;
  const anoNascimento = Number(String(dataNascimento).slice(0, 4));
  if (!anoNascimento) return null;
  return new Date().getFullYear() - anoNascimento;
}

function categoriaDoAluno(aluno) {
  const idade = idadeNoAno(aluno.data_nascimento);
  if (idade == null) return null;

  if (idade <= 13) return 'sub14';
  if (idade <= 15) return 'sub16';
  if (idade <= 17) return 'sub18';
  if (idade <= 20) return 'sub21';
  return 'adulto';
}

function sexoLabel(sexo) {
  if (sexo === 'masculino') return 'Masculino';
  if (sexo === 'feminino') return 'Feminino';
  return 'Não informado';
}

export default function CategoriasPage() {
  const [alunos, setAlunos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/gestor/alunos')
      .then(res => setAlunos(res.data || []))
      .catch(() => toast.error('Erro ao carregar categorias'))
      .finally(() => setLoading(false));
  }, []);

  const grupos = useMemo(() => {
    const base = {
      masculino: {},
      feminino: {},
      nao_informado: {},
    };

    Object.keys(base).forEach(sexo => {
      categorias.forEach(cat => {
        base[sexo][cat.key] = [];
      });
    });

    alunos.forEach(aluno => {
      const cat = categoriaDoAluno(aluno);
      if (!cat) return;

      const sexo = aluno.sexo === 'masculino' || aluno.sexo === 'feminino'
        ? aluno.sexo
        : 'nao_informado';

      base[sexo][cat].push(aluno);
    });

    return base;
  }, [alunos]);

  const totalClassificados = alunos.filter(a => categoriaDoAluno(a)).length;

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="categorias-page">
      <div>
        <h1 className="text-3xl font-black font-['Barlow_Condensed'] tracking-tight uppercase text-white">
          Categorias
        </h1>
        <p className="text-zinc-400 text-sm mt-1">
          Separação por sexo e categoria considerando a idade que o atleta completa no ano atual.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Card className="bg-[#141414] border-[#27272A]">
          <CardContent className="p-4">
            <p className="text-xs text-zinc-500 uppercase tracking-wider">Total de alunos</p>
            <p className="text-2xl font-bold text-white">{alunos.length}</p>
          </CardContent>
        </Card>
        <Card className="bg-[#141414] border-[#27272A]">
          <CardContent className="p-4">
            <p className="text-xs text-zinc-500 uppercase tracking-wider">Classificados</p>
            <p className="text-2xl font-bold text-white">{totalClassificados}</p>
          </CardContent>
        </Card>
        <Card className="bg-[#141414] border-[#27272A]">
          <CardContent className="p-4">
            <p className="text-xs text-zinc-500 uppercase tracking-wider">Ano base</p>
            <p className="text-2xl font-bold text-white">{new Date().getFullYear()}</p>
          </CardContent>
        </Card>
      </div>

      {loading ? (
        <Card className="bg-[#141414] border-[#27272A]">
          <CardContent className="p-8 text-center text-zinc-500">Carregando...</CardContent>
        </Card>
      ) : (
        ['feminino', 'masculino', 'nao_informado'].map(sexo => (
          <div key={sexo} className="space-y-3">
            <h2 className="text-xl font-bold text-white uppercase font-['Barlow_Condensed']">
              {sexoLabel(sexo)}
            </h2>

            <div className="grid grid-cols-1 lg:grid-cols-5 gap-3">
              {categorias.map(cat => {
                const lista = grupos[sexo][cat.key];

                return (
                  <Card key={`${sexo}-${cat.key}`} className="bg-[#141414] border-[#27272A]">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-white text-lg flex items-center justify-between">
                        {cat.label}
                        <Badge className="bg-[var(--cor-primaria,#007AFF)]/10 text-[var(--cor-primaria,#007AFF)] border-[var(--cor-primaria,#007AFF)]/20">
                          {lista.length}
                        </Badge>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      {lista.length === 0 ? (
                        <p className="text-xs text-zinc-500">Nenhum atleta</p>
                      ) : (
                        lista
                          .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                          .map(aluno => (
                            <div key={aluno.id} className="rounded-lg border border-[#27272A] bg-[#0A0A0A] p-2">
                              <p className="text-sm text-white font-medium">{aluno.name}</p>
                              <p className="text-xs text-zinc-500 flex items-center gap-1">
                                <Users className="w-3 h-3" />
                                {idadeNoAno(aluno.data_nascimento)} anos no ano
                              </p>
                            </div>
                          ))
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        ))
      )}
    </div>
  );
}
